import { DeliveryOrderStatusE, UserRoleE } from "../../../models/enums";
import { isDeliveryOrderPaid } from "../../../utils/helpers/isDeliveryOrderPaid";

/**
 * spec-41 D1/D2/D3 — the ONE condition behind every layout change on the delivery page for a
 * delivery-service customer whose delivery is finished and paid for.
 *
 * 🔴 Gated on the ROLE, never on `clientType === 5` (D1). `getUserRole` resolves
 * `UserRoleE.DeliveryService` for *any* user with `AccessLevel === DeliveryServicePrivate(4)`,
 * whatever their client type — so the two are different populations. Before spec 41 the logs panel
 * was gated on `clientType !== 5` while the status badge and the sidebar beside it were gated on
 * the role, and the two delivery-service modes silently diverged: a generic `JumboDelivery` user
 * (ClientType 5) saw no logs, while a private delivery-service user under an agency (ClientType 0,
 * AccessLevel 4) already saw the full Download + Activity Log panel. Keep one predicate.
 *
 * "Paid" delegates to `isDeliveryOrderPaid` (spec 26) — the single frontend definition, mirroring
 * the backend `PaidOrderGuard`. Do NOT write a second one here, and do not fold `Delivered(3)` in:
 * it is not paid to `PaidOrderGuard`, and counting it would create a competing definition of done.
 *
 * The frontend definition is narrower than the backend's (`PaymentVerifiedAt` is exposed by no view
 * model). That asymmetry is fine *here* because this is a DISPLAY decision, not an authorization
 * one — the worst case is a paid customer briefly shown the old wizard layout. Nobody should
 * "fix" it by loosening `isDeliveryOrderPaid`, which also drives the payment form.
 */
export const isDeliveryServiceView = (role?: UserRoleE): boolean =>
  role === UserRoleE.DeliveryService;

export const isCompletedDeliveryServiceView = (
  role?: UserRoleE,
  orderStatus?: DeliveryOrderStatusE | null
): boolean => isDeliveryServiceView(role) && isDeliveryOrderPaid(orderStatus);
